import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useClubSwitchOptions from '../../hooks/useClubSwitchOptions';
import { placeAnchoredMenu, subscribeViewport } from '../../service/placeAnchoredMenu';
import {
  StyledClubSwitcher,
  StyledClubTrigger,
  StyledClubTriggerName,
  StyledClubChevron,
  StyledClubOverlay,
  StyledClubMenu,
  StyledClubMenuLabel,
  StyledClubOption,
  StyledClubCrest,
  StyledClubOptionCopy,
  StyledClubOptionName,
  StyledClubOptionMeta,
  StyledClubCheck,
} from './styled';

const Chevron = () => (
  <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
    <path
      d="M1 1l4 4 4-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path
      d="M2.5 7.2l3 3.2 6-6.8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Crest = ({ src, name }) => (
  <StyledClubCrest>
    {src ? <img src={src} alt="" /> : <span>{(name || '?').slice(0, 1)}</span>}
  </StyledClubCrest>
);

function ClubSwitcher({ variant = 'header' }) {
  const {
    currentTeam,
    secondTeam,
    currentServer,
    secondServer,
    switchClub,
  } = useClubSwitchOptions();
  const triggerRef = useRef(null);
  const closingRef = useRef(false);
  const openedAtRef = useRef(0);
  const [rendered, setRendered] = useState(false);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({});

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    setCoords(
      placeAnchoredMenu(trigger, {
        align: variant === 'nav' ? 'start' : 'end',
        maxWidth: variant === 'nav' ? 360 : 280,
      })
    );
  }, [variant]);

  const closeMenu = useCallback(() => {
    closingRef.current = true;
    setOpen(false);
  }, []);

  const openMenu = useCallback(() => {
    closingRef.current = false;
    openedAtRef.current = Date.now();
    place();
    setRendered(true);
  }, [place]);

  useLayoutEffect(() => {
    if (!rendered || closingRef.current) return undefined;
    let inner;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setOpen(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [rendered]);

  useEffect(() => {
    if (!rendered) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') closeMenu();
    };
    const onReposition = () => place();
    window.addEventListener('keydown', onKey);
    const stopViewport = subscribeViewport(onReposition);
    return () => {
      window.removeEventListener('keydown', onKey);
      stopViewport();
    };
  }, [rendered, closeMenu, place]);

  useEffect(() => {
    if (open || !rendered || !closingRef.current) return undefined;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const id = window.setTimeout(() => setRendered(false), reduced ? 0 : 220);
    return () => window.clearTimeout(id);
  }, [open, rendered]);

  const onOverlayClick = () => {
    if (Date.now() - openedAtRef.current < 400) return;
    closeMenu();
  };

  const onMenuTransitionEnd = (event) => {
    if (event.propertyName !== 'opacity') return;
    if (event.currentTarget.getAttribute('data-open') !== 'true') {
      setRendered(false);
    }
  };

  const currentName = currentTeam?.name || 'Current Team';
  const triggerLabel = variant === 'nav' && currentServer
    ? `${currentName} · ${currentServer}`
    : currentName;

  const pick = (value) => {
    closeMenu();
    switchClub(value);
  };

  return (
    <StyledClubSwitcher $variant={variant}>
      <StyledClubTrigger
        ref={triggerRef}
        type="button"
        $variant={variant}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Switch club"
        onClick={() => (open ? closeMenu() : openMenu())}
      >
        <StyledClubTriggerName>{triggerLabel}</StyledClubTriggerName>
        <StyledClubChevron data-open={open ? 'true' : 'false'}>
          <Chevron />
        </StyledClubChevron>
      </StyledClubTrigger>
      {rendered &&
        createPortal(
          <>
            <StyledClubOverlay
              $open={open}
              onClick={onOverlayClick}
            />
            <StyledClubMenu
              $variant={variant}
              role="listbox"
              aria-label="Your clubs"
              data-open={open ? 'true' : 'false'}
              style={coords}
              onTransitionEnd={onMenuTransitionEnd}
            >
              <StyledClubMenuLabel>Your clubs</StyledClubMenuLabel>
              <StyledClubOption
                type="button"
                role="option"
                aria-selected="true"
                $active
                onClick={() => closeMenu()}
              >
                <Crest src={currentTeam?.symbol} name={currentName} />
                <StyledClubOptionCopy>
                  <StyledClubOptionName>{currentName}</StyledClubOptionName>
                  {currentServer ? (
                    <StyledClubOptionMeta>{currentServer}</StyledClubOptionMeta>
                  ) : null}
                </StyledClubOptionCopy>
                <StyledClubCheck>
                  <Check />
                </StyledClubCheck>
              </StyledClubOption>
              {secondTeam ? (
                <StyledClubOption
                  type="button"
                  role="option"
                  aria-selected="false"
                  onClick={() => pick('other')}
                >
                  <Crest src={secondTeam.symbol} name={secondTeam.name} />
                  <StyledClubOptionCopy>
                    <StyledClubOptionName>{secondTeam.name}</StyledClubOptionName>
                    {secondServer ? (
                      <StyledClubOptionMeta>{secondServer}</StyledClubOptionMeta>
                    ) : null}
                  </StyledClubOptionCopy>
                </StyledClubOption>
              ) : (
                <StyledClubOption
                  type="button"
                  role="option"
                  data-join="true"
                  onClick={() => pick('join')}
                >
                  <Crest name="+" />
                  <StyledClubOptionCopy>
                    <StyledClubOptionName>Join a second team</StyledClubOptionName>
                    <StyledClubOptionMeta>
                      Play on another server
                    </StyledClubOptionMeta>
                  </StyledClubOptionCopy>
                </StyledClubOption>
              )}
            </StyledClubMenu>
          </>,
          document.body
        )}
    </StyledClubSwitcher>
  );
}

export default React.memo(ClubSwitcher);
